'use client'

import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, Calendar, FileText, Trash2, ExternalLink } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import { useToast } from "@/hooks/use-toast"
import ReceiptService from '@/api/receipts.service'
import { useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
 
export type ReceiptType = {
  id: number
  receiptNumber: string
  amount: number
  currency: string
  paymentMethod: string
  paymentMethodName: string
  paymentDate: string
  timezone: string
  dateCreated: string
}

export const columns: ColumnDef<ReceiptType>[] = [
  {
    accessorKey: "receiptNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Receipt Number
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="flex items-center">
        <FileText className="w-4 h-4 mr-2 text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {cell.row.original.receiptNumber}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Date Created
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const date = new Date(cell.getValue() as string);
      const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      const formattedTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      
      return (
        <div className="text-slate-700 dark:text-slate-300">
          <div className="font-medium">{formattedDate}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{formattedTime}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "paymentMethodName",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Payment Method
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const paymentMethodName = cell.getValue() as string;
      const badgeConfig = {
        'CASH': {
          color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
          icon: '💵'
        },
        'BANK TRANSFER': {
          color: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
          icon: '🏦'
        }, 
        'MOBILE MONEY': {
          color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
          icon: '📱'
        },
        'CRYPTO': {
          color: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-violet-200 dark:border-violet-800',
          icon: '₿'
        }
      };
      const formattedName = paymentMethodName.replace('_', ' ').toUpperCase();
      const config = badgeConfig[formattedName as keyof typeof badgeConfig] || badgeConfig['CRYPTO'];
      
      return (
        <Badge variant="outline" className={`font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.color}`}>
          <span>{config.icon}</span>
          <span>{formattedName}</span>
        </Badge>
      );
    },
  },
  {
    accessorKey: "amount",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Amount
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="font-medium text-slate-900 dark:text-slate-100">
        {cell.row.original.currency} {cell.row.original.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    ),
  },
  {
    accessorKey: "paymentDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Payment Date
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      return (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-slate-400" />
          <span className="text-slate-700 dark:text-slate-300">{cell.row.original.paymentDate}</span>
        </div>
      );
    }
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const { toast } = useToast();
      const [showDeleteDialog, setShowDeleteDialog] = useState(false);
      
      const handleDelete = async () => {
        try {
          const response = await ReceiptService.deleteReceipt(row.original.id);
          if (response.data.result === 'success') {
            toast({
              title: "Receipt Deleted",
              description: "Receipt has been successfully deleted",
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast({
              title: "Error",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to delete receipt",
            variant: "destructive",
          });
        } finally {
          setShowDeleteDialog(false);
        }
      };

      return (
        <>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => window.location.href = `/receipt/${row.original.id}`} 
              variant="outline"
              className="h-8 px-3 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              View
            </Button>
            <Button 
              onClick={() => setShowDeleteDialog(true)} 
              variant="outline"
              className="h-8 px-3 text-rose-600 dark:text-rose-500 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/30"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              Delete
            </Button>
          </div>
          
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete receipt #{row.original.receiptNumber}. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      );
    }
  }
]
